import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import useSeo from "@/hooks/useSeo";

const CANTEEN = [
  ["Parties", "'Dietico' = operator of the Dietico platform (admin@dietico.co.in). 'You' = authorised hospital canteen representative. 'Hospital' = the clinical establishment."],
  ["Service description", "Dietico provides a digital diet order management platform enabling canteen administrators to upload daily menus and receive validated patient diet orders grouped by ward. For internal hospital workflow only."],
  ["Eligibility", "You are an authorised representative of a lawfully registered clinical establishment. The Hospital Canteen account is the prerequisite for all Nurse and Dietician accounts — you are responsible for communicating the Hospital Canteen ID to staff. One Hospital Canteen account per hospital is permitted in Version 1.0. Your hospital is registered or exempt under the Clinical Establishments (Registration and Regulation) Act, 2010."],
  ["Subscription & payment", "Subscription of ₹9,999 / month, auto-billed via Google Play or Apple App Store through RevenueCat. Covers the Hospital Canteen account and all associated Nurse and Dietician accounts. Grace period of up to 2 calendar months on payment failure. Non-renewal within 2 months results in permanent and irreversible deletion of all hospital data per the DPDP Act 2023. Email reminders during grace period. Refunds governed by Google Play / Apple App Store refund policy. Pricing changes notified 30 days in advance."],
  ["Data responsibilities", "You accept responsibility for ensuring all data entered by associated nurses and dieticians is accurate and authorised. Patient UHID and dietary data is personal data under the DPDP Act 2023. Data entry must be limited to legitimate clinical dietary management purposes."],
  ["Permitted use", "The app may only be used for internal hospital dietary management. Prohibited: false data, commercial use outside hospital operations, credential sharing, and accessing other hospitals' data."],
  ["PDF export", "Diet PDFs are clinical records. You are responsible for sharing them only with authorised clinical staff and storing them per your hospital's data protection obligations."],
  ["Security", "Screenshots are disabled in-app. You must not circumvent this. You are responsible for credential security. Dietico is not liable for unauthorised access due to compromised credentials."],
  ["Limitation of liability", "Dietico is a workflow tool, not a source of medical advice. All dietary decisions must be made by qualified professionals. Maximum aggregate liability shall not exceed total subscription fees paid in that calendar year."],
  ["Governing law", "Laws of India. Disputes first referred to the Grievance Officer. Unresolved disputes: exclusive jurisdiction of courts in Kolkata, West Bengal. Grievance Officer: Dr. Indranil Chatterjee · admin@dietico.co.in · +91 9002296906."],
];

const NURSE = [
  ["Eligibility", "You must be a registered nurse employed by a hospital with an active Dietico Hospital Canteen account. You must have been provided a valid Hospital Canteen ID by your hospital canteen administrator. You must be authorised by your hospital to enter patient dietary data. You must be 18 years of age or older."],
  ["Personal Hospital ID", "Your Personal Hospital ID is a private login credential you create at signup. It is not generated or verified by Dietico. It is unique to you, fixed after signup, and cannot be changed. Keep it confidential. Do not share it."],
  ["Patient data entry responsibilities", "All patient data you enter (UHID, age, gender, comorbidities, diagnosis, diet) must be accurate and entered in your capacity as an authorised healthcare professional. Inaccurate data may have clinical consequences for which Dietico bears no responsibility. Only enter data for patients under your clinical care during your duty shift. Patient discharge permanently and irreversibly deletes all that patient's data — only mark as discharged when certain."],
  ["Professional accountability", "You remain professionally accountable under the Indian Nursing Council Act, 1947 and your hospital's clinical governance policies for accuracy of information submitted."],
  ["Data privacy", "Patient UHID and clinical data is sensitive personal data under the DPDP Act 2023. Do not share it with unauthorised persons. Screenshots are disabled. You may only access patients in your assigned ward."],
  ["No medical advice", "Dietico is a workflow tool. Nutritional calculations (TDEE, protein, fat, carbs) are estimates based on Mifflin-St Jeor / NIN guidelines and must be reviewed by a qualified dietician before clinical application."],
  ["Governing law", "Laws of India. Grievance Officer: Dr. Indranil Chatterjee · admin@dietico.co.in · +91 9002296906."],
];

const DIETICIAN = [
  ["Eligibility", "You must be a registered dietician or clinical nutritionist authorised to practise in India, employed by or contracted to a hospital with an active Dietico Hospital Canteen account, and provided a valid Hospital Canteen ID by your hospital canteen administrator."],
  ["Digital signature", "By uploading your signature image and submitting a signed diet order: (a) your signature constitutes an electronic signature under the Information Technology Act, 2000, binding as a professional endorsement; (b) you confirm the signature image is your genuine professional signature; (c) you accept full professional accountability for each diet order you sign. Your signature is stored in a private encrypted bucket, accessed only via 5-minute TTL signed URLs, embedded in PDFs only when you generate a signed PDF, and not shared with third parties. IMPORTANT: this is NOT a Digital Signature Certificate (DSC) under the IT Act 2000; it is an electronic signature for internal hospital workflow only and does not carry the same legal weight as a DSC in medicolegal proceedings."],
  ["Diet order responsibilities", "Review all diet orders queued for signature. Orders queued 9 AM–5 PM IST on working days must be reviewed promptly. Do not sign without reviewing — your signature is your professional endorsement. All edits are version-logged (Edit 1, Edit 2, etc.) and stored for 1 year with your name. Edits to the Doctor-Prescribed Diet field are flagged as Significant Edits and reported monthly to the hospital canteen. The 'On Leave Today' toggle in your profile redirects all orders to the canteen for that day; it resets at midnight IST."],
  ["PDF generation", "Signed PDFs contain your signature stamp and constitute clinical records. Share only with authorised personnel. PDFs are generated on your device and deleted after sharing — not stored on Dietico's servers."],
  ["Professional standards", "Your use must comply with: (a) the Indian Dietetic Association Code of Ethics; (b) NIN Dietary Guidelines for Indians; (c) your hospital's clinical governance framework; (d) the Clinical Establishments Act, 2010."],
  ["FSSAI and NIN alignment", "Nutritional calculations are reference values based on the Mifflin-St Jeor BMR and NIN-aligned macronutrient distribution. As a qualified dietician you are responsible for interpreting and adjusting these values in each patient's clinical context."],
  ["Governing law", "Laws of India. Grievance Officer: Dr. Indranil Chatterjee · admin@dietico.co.in · +91 9002296906."],
];

const COMPLIANCE = [
  ["DPDP Act 2023", "Explicit consent; data minimisation; right to erasure; Indian data residency (AWS Mumbai / ap-south-1)."],
  ["IT Act 2000", "Electronic signatures documented under the IT Act, with limitations clearly stated in the Terms."],
  ["NIN Dietary Guidelines", "Nutritional calculations aligned to Indian standards — Mifflin-St Jeor BMR with NIN-aligned macro ratios."],
  ["FSSAI Guidelines", "1,200-item Indian food database references FSSAI-recognised categories and nutritional parameters."],
  ["Clinical Establishments Act 2010", "Diet order documentation with full audit trail and 1-year version history for registered clinical establishments."],
  ["Google Play Billing & Apple App Store 3.1.1", "Subscriptions handled via RevenueCat with Google Play Billing v6 and Apple StoreKit 2."],
  ["TRAI TCCCPR 2018", "No promotional push notifications; only operational messaging."],
];

function ClauseList({ items }) {
  return (
    <div className="divide-y divide-border rounded-2xl border border-border bg-secondary overflow-hidden">
      {items.map(([t, b], i) => (
        <div key={t} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 p-6 md:p-8">
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              {String(i + 1).padStart(2, "0")}
            </p>
            <p className="mt-2 text-foreground font-medium tracking-tight text-lg">
              {t}
            </p>
          </div>
          <div className="md:col-span-9">
            <p className="text-secondary leading-relaxed text-[15px]">{b}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Terms() {
  useSeo({
    title: "Terms & Conditions",
    description:
      "Terms & Conditions v1.0 for the Dietico platform — role-specific terms for Hospital Canteens, Nurses and Dieticians, and our regulatory compliance map.",
    path: "/terms",
  });
  return (
    <div data-testid="terms-page">
      <section className="mx-auto max-w-7xl px-6 pt-16 pb-12 md:pt-24">
        <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-6">
          Terms &amp; Conditions · Version 1.0
        </p>
        <h1 className="text-5xl md:text-7xl font-light tracking-tightest leading-[0.95] text-foreground max-w-4xl">
          The legal terms governing use of the Dietico platform.
        </h1>
        <p className="mt-8 max-w-3xl text-lg text-secondary leading-relaxed">
          Dietico is operated by Mesron Healthcare LLP. These Terms are
          organised by user role — Hospital Canteen, Nurse, and Dietician —
          and are followed by our compliance obligations.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24">
        <Tabs defaultValue="canteen" className="w-full">
          <TabsList
            data-testid="terms-tabs"
            className="grid grid-cols-2 md:grid-cols-4 w-full h-auto bg-secondary p-1 rounded-full"
          >
            <TabsTrigger
              value="canteen"
              data-testid="tab-canteen"
              className="rounded-full py-2.5 data-[state=active]:bg-background data-[state=active]:text-foreground"
            >
              Canteen
            </TabsTrigger>
            <TabsTrigger
              value="nurse"
              data-testid="tab-nurse"
              className="rounded-full py-2.5 data-[state=active]:bg-background data-[state=active]:text-foreground"
            >
              Nurse
            </TabsTrigger>
            <TabsTrigger
              value="dietician"
              data-testid="tab-dietician"
              className="rounded-full py-2.5 data-[state=active]:bg-background data-[state=active]:text-foreground"
            >
              Dietician
            </TabsTrigger>
            <TabsTrigger
              value="compliance"
              data-testid="tab-compliance"
              className="rounded-full py-2.5 data-[state=active]:bg-background data-[state=active]:text-foreground"
            >
              Compliance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="canteen" className="mt-10">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground mb-6">
              For Hospital Canteen Administrators
            </h2>
            <ClauseList items={CANTEEN} />
          </TabsContent>

          <TabsContent value="nurse" className="mt-10">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground mb-6">
              For Hospital Nurses
            </h2>
            <ClauseList items={NURSE} />
          </TabsContent>

          <TabsContent value="dietician" className="mt-10">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground mb-6">
              For Hospital Dieticians
            </h2>
            <ClauseList items={DIETICIAN} />
          </TabsContent>

          <TabsContent value="compliance" className="mt-10">
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground mb-6">
              Regulatory Compliance Map
            </h2>
            <p className="text-secondary leading-relaxed max-w-3xl mb-6">
              Dietico is designed and maintained to be compliant with the
              following Indian laws and platform policies. Each item below
              summarises the requirement and Dietico&rsquo;s implementation
              approach.
            </p>
            <ClauseList items={COMPLIANCE} />
          </TabsContent>
        </Tabs>

        <div className="mt-16 rounded-2xl border border-border bg-secondary p-8 md:p-10">
          <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-3">
            Questions or grievances
          </p>
          <p className="text-foreground text-lg tracking-tight">
            Contact Dr. Indranil Chatterjee, Grievance Officer, at{" "}
            <a
              href="mailto:admin@dietico.co.in"
              className="underline underline-offset-4 hover:opacity-80"
              data-testid="terms-email"
            >
              admin@dietico.co.in
            </a>{" "}
            or{" "}
            <a
              href="tel:+919002296906"
              className="underline underline-offset-4 hover:opacity-80"
              data-testid="terms-phone"
            >
              +91 9002296906
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
